import React, { useEffect, useState, FC, useContext } from 'react';
import {
  alpha,
  Box,
  Button,
  Checkbox,
  createTheme,
  FormControlLabel,
  FormGroup,
  hexToRgb,
  Link,
  Radio,
  RadioGroup,
  ThemeProvider,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import SurveyService from '../../services/SurveyService';
import { constants } from '../../constants/constants';
import { ISurvey } from '../../shared/interfaces/ISurvey';
import { LocalTranslationContext } from '../../hooks/useLocalTranslation';
import { useTranslation } from 'react-i18next';
import { CurrentViewContext } from '../../providers/CurrentViewProvider';
import StandardSurveyComment from './StandardSurveyComment';
import { ISurveyStandardQuestion } from '../../shared/interfaces/ISurveyStandardQuestion';
import SurveyUtils from '../../shared/SurveyUtils';
import {
  STANDARD_SURVEY_LAST_QUESTION_STEP,
  standardSurveyEndsAfterLastQuestion,
} from '../../shared/helpers/StandardSurveyFlow';
import Autolink from '../Autolink/Autolink';
import { SurveyRateUsButton } from '../SurveyRateUsButton/SurveyRateUsButton';
import { OpenInNew } from '@mui/icons-material';
import CssUtils from '../../shared/CssUtils';
import { useIsSurveyPreview } from '../../hooks/useIsSurveyPreview';

const StandardSurveyContent: FC<
  ISurvey & {
    shouldShowPrivacyNotice: boolean;
    privacyNoticeText: string;
    privacyNoticeLink: string;
  }
> = (props) => {
  const {
    primaryColor,
    secondaryColor,
    platform,
    platformUrl,
    platformName,
    id,
    questions,
    title,
    titleNegative,
    outroText,
    outroTextNegative,
    customFont,
    customFontFormat,
    score,
    introText,
    feedbackActive,
    feedbackTitle,
    feedbackText,
    answers,
    contactEmail,
    shouldShowPrivacyNotice,
    privacyNoticeText,
    privacyNoticeLink,
  } = props;
  const theme = useTheme();
  const effectiveFontFamily = '' + theme.typography.fontFamily;
  const localContext = useContext(LocalTranslationContext);
  const { t: globalT } = useTranslation();
  const t = localContext ? localContext.t : globalT;
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const isPreview = useIsSurveyPreview();
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [currentQuestion, setCurrentQuestion] = useState(questions || []);
  const [sum, setSum] = useState<number>(0);
  const { setCurrentView } = useContext(CurrentViewContext);
  const [lastAnswerId, setLastAnswerId] = useState<string>('');
  const [currentScore, setCurrentScore] = useState<number>(0);
  const [feedback, setFeedback] = useState<string>('');
  const [Submitted, setSubmitted] = useState<boolean>(false);
  const [privacyNoticeChecked, setPrivacyNoticeChecked] = useState<boolean>(false);
  const [questionsArray, setQuestionsArray] = useState<ISurveyStandardQuestion[]>([
    {
      questionId: currentQuestion[0]?.questionId || '',
      question: currentQuestion[0]?.question || '',
      answer: null,
      feedback: '',
    },
    {
      questionId: currentQuestion[1]?.questionId || '',
      question: currentQuestion[1]?.question || '',
      answer: null,
      feedback: '',
    },
    {
      questionId: currentQuestion[2]?.questionId || '',
      question: currentQuestion[2]?.question || '',
      answer: null,
      feedback: feedback,
    },
  ]);

  const handlePageChange = (change: number) => {
    setCurrentStep((prev) => prev + change);
    if (currentStep === 2) {
      const sum = questionsArray
        .slice(0, 2)
        .reduce((acc, question) => (question.answer ? acc + question.answer : 0), 0);
      setSum(sum);
    }
  };

  const saveSurvey = async () => {
    try {
      if (isPreview) return;
      const answers = await SurveyService.saveStandardSurveyAnswer(id, questionsArray as []);
      setLastAnswerId(answers[answers.length - 1].ID);
    } catch (error) {
      console.error('Fehler beim Speichern der Umfrage:', error);
    }
  };

  const updateSurvey = async () => {
    try {
      if (isPreview) return;
      if (lastAnswerId) {
        const allAnswers = questionsArray as [];
        await SurveyService.updateStandardSurveyAnswer(
          lastAnswerId,
          id,
          allAnswers[allAnswers.length - 1],
          { has_accepted_privacy_notice: privacyNoticeChecked }
        );
      }
    } catch (error) {
      console.error('Fehler beim Speichern der Umfrage:', error);
    }
  };

  const getFontFormatByFileExtension = (fontFormat: FontFileExtensions) => {
    return {
      woff2: 'woff2',
      woff: 'woff',
      ttf: 'truetype',
    }[fontFormat];
  };

  const customTheme = createTheme({
    palette: {
      primary: {
        main: primaryColor ? primaryColor : theme.palette.primary.main,
      },
      divider: secondaryColor ? alpha(hexToRgb(secondaryColor), 0.2) : theme.palette.divider,
    },
    typography: {
      fontWeightBold: 700,
      fontFamily: customFont && customFontFormat ? `"${id}"` : '"Arial", "Helvetica", sans-serif',
    },
    components: {
      MuiCssBaseline: {
        styleOverrides:
          customFont && customFontFormat
            ? `
           @font-face {
            weight: 700;
            font-family: "${id}";
            src: url(${customFont}) format("${getFontFormatByFileExtension(
                customFontFormat as FontFileExtensions
              )}");
           }
        `
            : undefined,
      },
    },
  });

  const handleQuestionChange = (questionScore: number, value: number) => {
    setQuestionsArray((prev) => {
      const updatedQuestions = [...prev];
      const question = updatedQuestions[value] ?? { answer: null };
      question.answer = questionScore;
      updatedQuestions[value] = question;
      return updatedQuestions;
    });
  };

  const renderQuestion = (step: number) => {
    if (currentStep === step) {
      return (
        <ThemeProvider theme={customTheme}>
          <Box className="survey-question-step survey-question-step--standard">
            <Typography
              className="survey-intro-text"
              variant="h5"
              sx={{
                color: primaryColor,
                marginBottom: theme.spacing(5),
                alignItems: 'center',
                fontWeight: 'bold',
                fontSize: theme.spacing(4),
                fontFamily: effectiveFontFamily,
              }}
            >
              {introText}
            </Typography>
            <Typography
              className="survey-question-text"
              variant="h5"
              sx={{
                color: secondaryColor,
                marginBottom: theme.spacing(5),
                alignItems: 'center',
                whiteSpace: 'pre-wrap',
                fontFamily: effectiveFontFamily,
              }}
            >
              {questionsArray[step]?.question}
            </Typography>
            {answers?.map((answer, index) => (
              <Box
                className="survey-answer-group"
                key={index}
              >
                <FormGroup className="survey-answer-form-group">
                  <RadioGroup className="survey-answer-options survey-answer-options--standard">
                    <FormControlLabel
                      className="survey-answer-option"
                      control={<Radio />}
                      label={answer.answerOne}
                      value={5}
                      onClick={() => handleQuestionChange(5, step)}
                    />
                    <FormControlLabel
                      className="survey-answer-option"
                      control={<Radio />}
                      label={answer.answerTwo}
                      value={4}
                      onClick={() => handleQuestionChange(4, step)}
                    />
                    <FormControlLabel
                      className="survey-answer-option"
                      control={<Radio />}
                      label={answer.answerThree}
                      value={3}
                      onClick={() => handleQuestionChange(3, step)}
                    />
                    <FormControlLabel
                      className="survey-answer-option"
                      control={<Radio />}
                      label={answer.answerFour}
                      value={2}
                      onClick={() => handleQuestionChange(2, step)}
                    />
                    <FormControlLabel
                      className="survey-answer-option"
                      control={<Radio />}
                      label={answer.answerFive}
                      value={1}
                      onClick={() => handleQuestionChange(1, step)}
                    />
                  </RadioGroup>
                </FormGroup>
              </Box>
            ))}
          </Box>
        </ThemeProvider>
      );
    }
  };

  const renderLastQuestion = (step: number) => {
    if (currentStep === step) {
      return (
        <ThemeProvider theme={customTheme}>
          <Box className="survey-question-step survey-question-step--standard survey-question-step--last">
            <Typography
              className="survey-intro-text"
              variant="h5"
              sx={{
                color: primaryColor,
                marginBottom: theme.spacing(5),
                alignItems: 'center',
                fontWeight: 'bold',
                fontSize: theme.spacing(4),
                fontFamily: effectiveFontFamily,
              }}
            >
              {introText}
            </Typography>
            <Typography
              className="survey-question-text"
              variant="h5"
              sx={{
                color: secondaryColor,
                marginBottom: theme.spacing(5),
                alignItems: 'center',
                fontFamily: effectiveFontFamily,
              }}
            >
              {questionsArray[step]?.question}
            </Typography>
            <FormGroup className="survey-answer-form-group">
              <RadioGroup className="survey-answer-options survey-answer-options--yes-no">
                <FormControlLabel
                  className="survey-answer-option survey-answer-option--yes"
                  control={<Radio />}
                  label={t('yes')}
                  value={1}
                  onClick={() => handleQuestionChange(1, step)}
                />
                <FormControlLabel
                  className="survey-answer-option survey-answer-option--no"
                  control={<Radio />}
                  label={t('no')}
                  value={0}
                  onClick={() => handleQuestionChange(0, step)}
                />
              </RadioGroup>
            </FormGroup>
          </Box>
        </ThemeProvider>
      );
    }
  };

  const renderPositivOutro = () => {
    return (
      <Box
        className="survey-outro survey-outro--positive"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          textAlign: 'center',
        }}
      >
        <Typography
          className="survey-title survey-outro-title"
          variant="h4"
          sx={{
            color: primaryColor,
            marginBottom: theme.spacing(5),
            fontWeight: 'bold',
            fontFamily: effectiveFontFamily,
          }}
        >
          {title}
        </Typography>
        <Typography
          className="survey-outro-text"
          variant="h5"
          sx={{
            color: secondaryColor,
            marginBottom: theme.spacing(5),
            alignItems: 'center',
            fontFamily: effectiveFontFamily,
          }}
        >
          <Autolink text={outroText} />
        </Typography>
        {platform && platformUrl && (
          <Box className="survey-rate-us-wrapper">
            <SurveyRateUsButton
              surveyId={id}
              surveyType={'standard'}
              platformUrl={platformUrl}
              platform={platform ?? ''}
              platformName={platformName}
              theme={theme}
              primary={primaryColor}
              customFont={effectiveFontFamily}
            ></SurveyRateUsButton>
          </Box>
        )}
      </Box>
    );
  };

  const renderFeedbackPrivacy = () => {
    if (!shouldShowPrivacyNotice) return <></>;

    return (
      <Box className="survey-privacy-notice">
        <Typography className="survey-privacy-notice-text">
          <Checkbox
            className="survey-privacy-notice-checkbox"
            checked={privacyNoticeChecked}
            onChange={() => setPrivacyNoticeChecked((prev) => !prev)}
          ></Checkbox>
          {privacyNoticeText}{' '}
          <a
            href={privacyNoticeLink}
            target="_blank"
            rel="noreferrer"
          >
            <OpenInNew
              fontSize="small"
              color="primary"
            ></OpenInNew>
          </a>
        </Typography>
      </Box>
    );
  };

  const renderNegativOutro = () => {
    let tmpTitle = titleNegative;
    if (!tmpTitle) {
      tmpTitle = title;
    }
    let tmpOutroText = outroTextNegative;
    if (!tmpOutroText) {
      tmpOutroText = outroText;
    }
    return (
      <Box
        className="survey-outro survey-outro--negative"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          textAlign: 'center',
        }}
      >
        <Typography
          className="survey-title survey-outro-title"
          variant="h4"
          sx={{
            color: primaryColor,
            marginBottom: theme.spacing(5),
            fontWeight: 'bold',
            fontFamily: effectiveFontFamily,
          }}
        >
          {tmpTitle}
        </Typography>
        <Typography
          className="survey-outro-text"
          variant="h5"
          sx={{
            color: secondaryColor,
            marginBottom: theme.spacing(5),
            alignItems: 'center',
            fontFamily: effectiveFontFamily,
          }}
        >
          <Autolink text={tmpOutroText} />
        </Typography>
        {contactEmail && !feedbackActive && (
          <Typography
            className="survey-contact-email"
            variant="h5"
            sx={{
              color: secondaryColor,
              marginBottom: theme.spacing(3),
              alignItems: 'center',
              mt: 4,
              fontFamily: effectiveFontFamily,
            }}
          >
            {contactEmail ? `${t('appreciate_feedback')}: ${contactEmail}` : null}
          </Typography>
        )}
      </Box>
    );
  };

  const renderFeedbackComment = () => {
    let tmpTitle = titleNegative;
    if (!tmpTitle) {
      tmpTitle = title;
    }
    return (
      <>
        <Box className="survey-feedback survey-feedback--standard">
          <StandardSurveyComment
            {...{
              secondaryColor,
              primaryColor,
              customFont: effectiveFontFamily,
              feedbackText,
              feedbackTitle,
              title: tmpTitle,
            }}
            onChange={setFeedback}
          />
          {renderFeedbackPrivacy()}
        </Box>
        <Box
          className="survey-navigation survey-navigation--feedback"
          display="flex"
          justifyContent="flex-end"
          width="100%"
        >
          <Button
            className="survey-button survey-button--send survey-button--standard-feedback"
            variant="contained"
            sx={{
              backgroundColor: primaryColor,
              width: '120px',
              padding: theme.spacing(2),
            }}
            disabled={shouldShowPrivacyNotice && !privacyNoticeChecked && !!feedback}
            onClick={() => {
              updateSurvey();
              setSubmitted(true);
              handlePageChange(1);
            }}
          >
            {t('send')}
          </Button>
        </Box>
      </>
    );
  };

  useEffect(() => {
    setCurrentView(t('survey'));
    document.title = `${t('app_branding')} ${t('survey')}`;
  }, [setCurrentView, t]);

  useEffect(() => {
    if (questions) {
      setCurrentScore(questions[0].score);
    }
  }, [score]);

  useEffect(() => {
    questionsArray[2].feedback = feedback;
  }, [feedback]);

  useEffect(() => {
    const newSum = questionsArray
      .slice(0, 2)
      .reduce((acc, question) => acc + (question.answer || 0), 0);
    setSum(newSum);
  }, [questionsArray, sum]);

  const isLastQuestionStep = currentStep === STANDARD_SURVEY_LAST_QUESTION_STEP;
  const submitsOnClick =
    isLastQuestionStep &&
    standardSurveyEndsAfterLastQuestion(questionsArray, currentScore, feedbackActive);

  return (
    <Box
      className="survey-content survey-content--standard"
      sx={CssUtils.surveyContent()}
    >
      {renderQuestion(0)}
      {renderQuestion(1)}
      {renderLastQuestion(2)}
      {currentStep === 3 && sum >= currentScore && questionsArray[2].answer === 1 && Submitted
        ? renderPositivOutro()
        : null}
      {feedbackActive && !Submitted && currentStep === 3 ? renderFeedbackComment() : null}
      {(Submitted && questionsArray[2].answer === 0) || (sum < currentScore && Submitted)
        ? renderNegativOutro()
        : null}
      <Box
        className="survey-navigation survey-navigation--standard"
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: currentStep === 0 || currentStep === 3 ? 'flex-end' : 'space-between',
          alignItems: 'center',
          textAlign: 'center',
          width: '100%',
          mt: theme.spacing(4),
        }}
      >
        {currentStep < 3 && currentStep !== 0 && (
          <Button
            className="survey-button survey-button--back"
            variant="contained"
            sx={{
              backgroundColor: primaryColor,
              width: '120px',
              padding: theme.spacing(2),
            }}
            onClick={() => handlePageChange(-1)}
          >
            {t('back')}
          </Button>
        )}
        {currentStep < 3 && (
          <Button
            className={`survey-button survey-button--${
              isLastQuestionStep ? 'send' : 'next'
            } survey-button--standard-step-${currentStep + 1}`}
            variant="contained"
            disabled={questionsArray[currentStep]?.answer === null}
            sx={{
              backgroundColor: primaryColor,
              width: '120px',
              padding: theme.spacing(2),
            }}
            onClick={() => {
              if (isLastQuestionStep) {
                saveSurvey();
              }
              if (submitsOnClick) {
                setSubmitted(true);
              }
              handlePageChange(1);
            }}
          >
            {isLastQuestionStep ? t('confirm') : t('next')}
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default StandardSurveyContent;
